import {
  Component,
  DestroyRef,
  input,
  output,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  CommunicationUserType,
  DriverSuggestionType,
  SuggestionPriority,
  SuggestionStatus,
  SuggestionType,
} from 'src/app/models/enums';
import { ISuggestion } from 'src/app/models/suggestion';
import { LiteralService, UtilsService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { ISuggestionState } from 'src/app/store/states/suggestions';
import { images } from 'src/images';
import {
  suggestionPriorityIcon,
  suggestionStatusIcon,
  suggestionUserResolutionBadgeClass,
  suggestionVisibilityIcon,
  suggestionWithoutSuggesterLiteral,
} from 'src/app/utils/suggestionTickets';

@Component({
  selector: 'app-suggestions-table',
  templateUrl: './suggestions-table.component.html',
  styleUrl: './suggestions-table.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SuggestionsTableComponent {
  readonly suggestions = input<ISuggestionState>();
  readonly userType = input<CommunicationUserType>();
  readonly selectedSuggestionId = input<number>();
  readonly selectSuggestion = output<any>();
  readonly changePriority = output<any>();
  readonly changeStatus = output<any>();

  public images = images;
  public CommunicationUserType = CommunicationUserType;
  public SuggestionTypes = SuggestionType;
  public DriverSuggestionTypes = DriverSuggestionType;
  public SuggestionStatus = SuggestionStatus;
  public SuggestionPriorities = SuggestionPriority;
  public TooltipPosition = TooltipPosition;
  public suggestionVisibilityIcon = suggestionVisibilityIcon;
  public suggestionPriorityIcon = suggestionPriorityIcon;
  public suggestionStatusIcon = suggestionStatusIcon;
  public suggestionUserResolutionBadgeClass = suggestionUserResolutionBadgeClass;

  public showPriorityOptions: number | null = null;
  public showStatusOptions: number | null = null;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    private renderer: Renderer2,
    destroyRef: DestroyRef,
  ) {
    const removeWindowClickListener = renderer.listen('window', 'click', (event: Event) => {
      let modal = document.getElementById('suggestionsContainer')!;
      if (this.showPriorityOptions) {
        modal = document.getElementById('prioritiesContainer')!;
      } else if (this.showStatusOptions) {
        modal = document.getElementById('statusesContainer')!;
      }
      const close = utilsService.closeCustomModals(event, modal, modal);
      if (close) {
        if (this.showPriorityOptions) this.showPriorityOptions = null;
        if (this.showStatusOptions) this.showStatusOptions = null;
      }
    });
    destroyRef.onDestroy(removeWindowClickListener);
  }

  async showSuggestionData(id: number, edit?: boolean) {
    this.selectSuggestion.emit({ id, edit });
  }

  togglePriorityModal(event: any, id: number) {
    event.stopImmediatePropagation();
    event.preventDefault();
    if (this.showPriorityOptions === id) this.showPriorityOptions = null;
    else {
      if (this.showStatusOptions) this.showStatusOptions = null;
      this.showPriorityOptions = id;
    }
  }

  updatePriority(id: number, value: string) {
    this.changePriority.emit({ id, value });
    this.showPriorityOptions = null;
  }

  toggleStatusModal(event: any, id: number) {
    event.stopImmediatePropagation();
    event.preventDefault();
    if (this.showStatusOptions === id) this.showStatusOptions = null;
    else {
      if (this.showPriorityOptions) this.showPriorityOptions = null;
      this.showStatusOptions = id;
    }
  }

  updateStatus(id: number, value: string) {
    this.changeStatus.emit({ id, value });
    this.showStatusOptions = null;
  }

  getUserSuggesterName(suggestion: ISuggestion): string {
    if (suggestion.userSuggester?.id) {
      return `${suggestion.userSuggester.firstName} ${suggestion.userSuggester.lastName}`;
    } else {
      return this.literalService.get(suggestionWithoutSuggesterLiteral(suggestion), true);
    }
  }

  visibilityTooltip(suggestion: ISuggestion): string {
    const visibility = suggestion.visibility ?? 'PRIVATE';
    return `<strong>${this.literalService.get(`suggestions.visibilities.${visibility}`, true)}</strong></br>${this.literalService.get(`suggestions.visibilityTooltips.${visibility}`, true)}`;
  }
}
